import React, { useEffect, useRef, useState } from "react";
import { Linkedin, Youtube, Instagram, Menu, X, ChevronDown } from "lucide-react";
import { TEST_IDS } from "@/lib/testIds";
import { LINKS } from "@/lib/links";
import { LogoCompact } from "@/components/site/Logo";

// Single top-level link
const aboutLink = {
  label: "About",
  href: "/about",
  testId: "nav-link-about",
};

// Dropdowns
const workWithMeItems = [
  { label: "Policy Review", href: "/policy-review", testId: "nav-work-policy-review" },
  { label: "Strategy Profile", href: "/strategy-profile", testId: "nav-work-strategy-profile" },
  {
    label: "Book a Call",
    href: LINKS.calendly,
    external: true,
    testId: "nav-work-book-call",
    accent: true,
  },
];

const resourcesItems = [
  { label: "Articles", href: "/resources", testId: "nav-resources-articles" },
  { label: "FAQ", href: "/faq", testId: "nav-resources-faq" },
];

const learnItems = [
  { label: "How It Works", href: "/how-it-works", testId: "nav-learn-how-it-works" },
  { label: "Take the Assessment", href: "/quiz", testId: "nav-learn-quiz", accent: true },
  { label: "Get the Free Guide", href: "/free-guide", testId: "nav-learn-free-guide" },
];

const socials = [
  { href: LINKS.linkedin, Icon: Linkedin, label: "LinkedIn", testId: TEST_IDS.nav.socialLinkedin },
  { href: LINKS.youtube, Icon: Youtube, label: "YouTube", testId: TEST_IDS.nav.socialYoutube },
  { href: LINKS.instagram, Icon: Instagram, label: "Instagram", testId: TEST_IDS.nav.socialInstagram },
];

// Dropdown menu (desktop). Hover + click + keyboard close.
const Dropdown = ({ label, items, testId, light, openId, setOpenId, id }) => {
  const ref = useRef(null);
  const hoverTimer = useRef(null);
  const isOpen = openId === id;

  useEffect(() => {
    if (!isOpen) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpenId(null);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpenId(null); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [isOpen, setOpenId]);

  const open = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setOpenId(id);
  };
  const scheduleClose = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setOpenId(null), 180);
  };

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={open}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        onClick={() => setOpenId(isOpen ? null : id)}
        data-testid={testId}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        className={`inline-flex items-center gap-1.5 text-[11px] tracking-[0.22em] uppercase font-medium transition-colors link-underline ${
          light ? "text-paper hover:text-camellight" : "text-nearblack hover:text-camel"
        }`}
      >
        {label}
        <ChevronDown
          className={`w-3 h-3 transition-transform ${isOpen ? "rotate-180" : ""}`}
          strokeWidth={2}
        />
      </button>
      {isOpen && (
        <div
          role="menu"
          data-testid={`${testId}-menu`}
          className="absolute left-1/2 -translate-x-1/2 top-full mt-3 min-w-[230px] bg-paper border border-paperdeep shadow-[0_18px_40px_-20px_rgba(28,26,23,0.35)]"
          onMouseEnter={open}
          onMouseLeave={scheduleClose}
        >
          <ul className="py-3">
            {items.map((item) => (
              <li key={item.testId}>
                <a
                  href={item.href}
                  data-testid={item.testId}
                  onClick={() => setOpenId(null)}
                  role="menuitem"
                  target={item.external ? "_blank" : undefined}
                  rel={item.external ? "noopener noreferrer" : undefined}
                  className={`block px-5 py-3 text-[11px] tracking-[0.22em] uppercase font-medium transition-colors ${
                    item.accent
                      ? "text-camel hover:bg-paperdeep/40"
                      : "text-nearblack hover:bg-paperdeep/40"
                  }`}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export const Nav = ({ heroVisible = true }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // 'work' | 'resources' | 'learn' | null

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const light = !scrolled && heroVisible;

  return (
    <header
      data-testid={TEST_IDS.nav.root}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-700 ${
        scrolled
          ? "bg-paper/90 backdrop-blur-md border-b border-paperdeep"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 h-20 md:h-24 flex items-center justify-between gap-6">
        <a
          href="/"
          data-testid={TEST_IDS.nav.brand}
          className="whitespace-nowrap"
        >
          <LogoCompact light={light} />
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          <a
            href={aboutLink.href}
            data-testid={aboutLink.testId}
            className={`text-[11px] tracking-[0.22em] uppercase font-medium transition-colors link-underline ${
              light ? "text-paper hover:text-camellight" : "text-nearblack hover:text-camel"
            }`}
          >
            {aboutLink.label}
          </a>

          <Dropdown
            label="Work With Me"
            items={workWithMeItems}
            testId="nav-work-trigger"
            id="work"
            light={light}
            openId={openDropdown}
            setOpenId={setOpenDropdown}
          />

          <Dropdown
            label="Resources"
            items={resourcesItems}
            testId="nav-resources-trigger"
            id="resources"
            light={light}
            openId={openDropdown}
            setOpenId={setOpenDropdown}
          />

          <Dropdown
            label="Learn"
            items={learnItems}
            testId="nav-learn-trigger"
            id="learn"
            light={light}
            openId={openDropdown}
            setOpenId={setOpenDropdown}
          />
        </nav>

        <div className="hidden md:flex items-center gap-5">
          <div className="hidden md:flex items-center gap-3 pr-1 border-r border-paperdeep/40">
            {socials.map(({ href, Icon, label, testId }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={testId}
                className={`transition-colors pr-3 ${
                  light ? "text-paper/80 hover:text-camellight" : "text-nearblack/60 hover:text-camel"
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.5} />
              </a>
            ))}
          </div>
          <a
            href={LINKS.calendly}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={TEST_IDS.nav.cta}
            className="btn-camel whitespace-nowrap"
          >
            Work with Liz
          </a>
        </div>

        <button
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          className={`md:hidden p-2 ${light ? "text-paper" : "text-nearblack"}`}
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-paper border-t border-paperdeep">
          <div className="px-6 py-8 flex flex-col gap-6">
            {/* About */}
            <a
              href={aboutLink.href}
              onClick={() => setOpen(false)}
              data-testid={`${aboutLink.testId}-mobile`}
              className="text-[12px] tracking-[0.22em] uppercase text-nearblack hover:text-camel"
            >
              {aboutLink.label}
            </a>

            {/* Work With Me */}
            <MobileGroup
              title="Work With Me"
              items={workWithMeItems}
              onItemClick={() => setOpen(false)}
            />

            {/* Resources */}
            <MobileGroup
              title="Resources"
              items={resourcesItems}
              onItemClick={() => setOpen(false)}
            />

            {/* Learn */}
            <MobileGroup
              title="Learn"
              items={learnItems}
              onItemClick={() => setOpen(false)}
            />

            <div className="flex items-center gap-5 pt-2 border-t border-paperdeep/60">
              {socials.map(({ href, Icon, label, testId }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`${testId}-mobile`}
                  className="text-nearblack/60"
                >
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                </a>
              ))}
            </div>
            <a
              href={LINKS.calendly}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              data-testid={`${TEST_IDS.nav.cta}-mobile`}
              className="mt-2 btn-camel text-center"
            >
              Work with Liz
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

const MobileGroup = ({ title, items, onItemClick }) => (
  <div className="pt-3 border-t border-paperdeep/60">
    <p
      className="text-[10px] tracking-[0.32em] uppercase text-stonegrey mb-3"
      style={{ fontWeight: 500 }}
    >
      {title}
    </p>
    <div className="flex flex-col gap-4 pl-1">
      {items.map((item) => (
        <a
          key={item.testId}
          href={item.href}
          onClick={onItemClick}
          target={item.external ? "_blank" : undefined}
          rel={item.external ? "noopener noreferrer" : undefined}
          data-testid={`${item.testId}-mobile`}
          className={`text-[12px] tracking-[0.22em] uppercase ${
            item.accent ? "text-camel hover:text-nearblack" : "text-nearblack hover:text-camel"
          }`}
        >
          {item.label}
        </a>
      ))}
    </div>
  </div>
);
